/**
 * Seed-RNG движка: xoshiro128** на 32-битных словах.
 *
 * Seed-строка → FNV-1a (32 бит) → splitmix32 → четыре слова состояния.
 * `fork(label)` создаёт независимый поток из `seed + '/' + label`,
 * не потребляя значений родителя — поэтому порядок вызовов в одной
 * подсистеме не влияет на случайности другой.
 *
 * Никаких Math.random() и Date.now(): один и тот же seed даёт одну и ту же
 * последовательность на клиенте и на сервере.
 */

export interface Rng {
  /** Равномерное число в [0, 1). */
  next(): number;
  /** Целое в [min, max] включительно. */
  int(min: number, max: number): number;
  /** true с вероятностью p; chance(0) всегда false, chance(1) всегда true. */
  chance(p: number): boolean;
  /** Равновероятный выбор элемента непустого списка. */
  pick<T>(items: readonly T[]): T;
  /** Выбор пропорционально неотрицательным весам (хотя бы один > 0). */
  weighted<T>(items: readonly T[], weights: readonly number[]): T;
  /** Нормальное распределение N(mean, sd²), Box–Muller. */
  normal(mean: number, sd: number): number;
  /** Логнормальное: exp(N(mu, sigma²)). */
  logNormal(mu: number, sigma: number): number;
  /** Независимый детерминированный поток из того же seed. */
  fork(label: string): Rng;
}

const TWO_POW_32 = 4294967296;

/** FNV-1a, 32 бита, по кодовым единицам UTF-16. */
function fnv1a32(text: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** splitmix32: из одного 32-битного зерна выдаёт последовательность слов. */
function splitmix32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x9e3779b9) >>> 0;
    let z = state;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
    z = z ^ (z >>> 15);
    return z >>> 0;
  };
}

function rotl(x: number, k: number): number {
  return ((x << k) | (x >>> (32 - k))) >>> 0;
}

/** xoshiro128**: генератор 32-битных слов на состоянии из четырёх слов. */
function xoshiro128ss(seedWord: number): () => number {
  const mix = splitmix32(seedWord);
  let s0 = mix();
  let s1 = mix();
  let s2 = mix();
  let s3 = mix();
  // Состояние из одних нулей недопустимо; splitmix практически его не даёт,
  // но страхуемся явно.
  if ((s0 | s1 | s2 | s3) === 0) s0 = 1;

  return () => {
    const result = Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0;
    const t = (s1 << 9) >>> 0;
    s2 ^= s0;
    s3 ^= s1;
    s1 ^= s2;
    s0 ^= s3;
    s2 ^= t;
    s3 = rotl(s3, 11);
    return result;
  };
}

function normalizeSeed(seed: string | number): string {
  return typeof seed === 'number' ? String(seed) : seed;
}

export function createRng(seed: string | number): Rng {
  const seedString = normalizeSeed(seed);
  const nextWord = xoshiro128ss(fnv1a32(seedString));

  const next = (): number => nextWord() / TWO_POW_32;

  const rng: Rng = {
    next,

    int(min, max) {
      const lo = Math.ceil(Math.min(min, max));
      const hi = Math.floor(Math.max(min, max));
      const span = hi - lo + 1;
      return lo + Math.floor(next() * span);
    },

    chance(p) {
      if (p <= 0) return false;
      if (p >= 1) return true;
      return next() < p;
    },

    pick(items) {
      if (items.length === 0) {
        throw new RangeError('rng.pick: empty list');
      }
      const index = Math.floor(next() * items.length);
      return items[index] as (typeof items)[number];
    },

    weighted(items, weights) {
      if (items.length === 0) {
        throw new RangeError('rng.weighted: empty list');
      }
      if (items.length !== weights.length) {
        throw new RangeError('rng.weighted: items and weights length mismatch');
      }
      let total = 0;
      for (const w of weights) {
        if (!(w >= 0) || !Number.isFinite(w)) {
          throw new RangeError('rng.weighted: weights must be finite and >= 0');
        }
        total += w;
      }
      if (total <= 0) {
        throw new RangeError('rng.weighted: total weight must be > 0');
      }
      let r = next() * total;
      let lastPositive = 0;
      for (let i = 0; i < weights.length; i += 1) {
        const w = weights[i] as number;
        if (w <= 0) continue;
        lastPositive = i;
        if (r < w) return items[i] as (typeof items)[number];
        r -= w;
      }
      // Ошибки округления: возвращаем последний элемент с положительным весом.
      return items[lastPositive] as (typeof items)[number];
    },

    normal(mean, sd) {
      // Box–Muller; u1 строго в (0, 1), чтобы log не ушёл в -Infinity.
      let u1 = next();
      while (u1 <= 0) u1 = next();
      const u2 = next();
      const mag = Math.sqrt(-2 * Math.log(u1));
      return mean + sd * mag * Math.cos(2 * Math.PI * u2);
    },

    logNormal(mu, sigma) {
      return Math.exp(rng.normal(mu, sigma));
    },

    fork(label) {
      return createRng(`${seedString}/${label}`);
    },
  };

  return rng;
}
